import { useState, useCallback } from 'react';
import { z } from 'zod';

interface UseZodValidationProps<T> {
  schema: z.ZodSchema<T>;
  initialData?: Partial<T>;
}

interface ValidationResult<T> {
  data: T | null;
  errors: Record<string, string>;
  isValid: boolean;
  validate: (data: Partial<T>) => boolean;
  clearErrors: () => void;
  clearFieldError: (field: string) => void;
}

export function useZodValidation<T>({ 
  schema, 
  initialData 
}: UseZodValidationProps<T>): ValidationResult<T> {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [data, setData] = useState<T | null>(null);


  const validate = useCallback((formData: Partial<T>): boolean => {
    try {
      const result = schema.parse(formData);
      setData(result);
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        
        error.errors.forEach((err) => {
          const field = err.path.join('.');
          fieldErrors[field] = err.message;
        });
        
        setErrors(fieldErrors);
        setData(null);
        return false;
      }
      return false;
    }
  }, [schema]);

  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  const clearFieldError = useCallback((field: string) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  return {
    data,
    errors,
    isValid: Object.keys(errors).length === 0,
    validate,
    clearErrors,
    clearFieldError
  };
}
